import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  SimpleGrid,
} from "@chakra-ui/react";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT } from "constants/brandColors";
import { getTranslatedField, useLocales, useTranslationsState, validateNameTranslations } from "utils/translations";
import LocaleNameTabs from "components/localeNameFields/LocaleNameTabs";

const SUBCATEGORIES_PAGE_ID = PAGE_IDS.SUBCATEGORIES;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

const emptyNameTranslation = () => ({ name: "" });

function CreateSubCategory({ onCreated }) {
  const { t } = useTranslation();
  const { locales } = useLocales();
  const [translations, setTranslations, setTranslationField] = useTranslationsState(locales, emptyNameTranslation);
  const [categoryId, setCategoryId] = useState("");
  const [slug, setSlug] = useState("");
  const [order, setOrder] = useState("");
  const [categories, setCategories] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/category`);
        setCategories(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const errors = validateNameTranslations(translations, locales, t);
    if (!categoryId) errors.categoryId = t("Category selection is required");
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const translationsPayload = locales
        .map(({ code }) => ({ locale: code, name: (translations[code]?.name || "").trim() }))
        .filter((tr) => tr.name);

      const data = {
        page_id: SUBCATEGORIES_PAGE_ID,
        translations: translationsPayload,
        categoryId: Number(categoryId),
        ...(slug.trim() && { slug: slug.trim() }),
        ...(order !== "" && { order: Number(order) }),
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/sub/category`,
        "POST",
        data
      );
      if (!response) {
        throw new Error("Failed to create sub category");
      }
      setTranslations(() => {
        const reset = {};
        locales.forEach(({ code }) => { reset[code] = emptyNameTranslation(); });
        return reset;
      });
      setCategoryId("");
      setSlug("");
      setOrder("");
      setFieldErrors({});
      onCreated?.();
    } catch (err) {
      setError(err.message || t("Failed to create sub category. Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormControl>
      <form onSubmit={handleSubmit}>
        <LocaleNameTabs
          locales={locales}
          translations={translations}
          errors={fieldErrors}
          onChange={(code, value) => setTranslationField(code, "name", value)}
          borderColor={GOLD_BORDER}
        />

        <FormControl isInvalid={!!fieldErrors.categoryId} mt="16px">
          <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Category")}</FormLabel>
          <Select isRequired placeholder={t("Select Category")} value={categoryId} onChange={(e) => setCategoryId(e.target.value)} {...fieldProps}>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>{getTranslatedField(category.translations, "ar")}</option>
            ))}
          </Select>
          <FormErrorMessage>{fieldErrors.categoryId}</FormErrorMessage>
        </FormControl>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt="16px">
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Slug")}</FormLabel>
            <Input placeholder={t("Leave blank to auto-generate")} value={slug} onChange={(e) => setSlug(e.target.value)} {...fieldProps} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Order")}</FormLabel>
            <Input type="number" placeholder="0" value={order} onChange={(e) => setOrder(e.target.value)} {...fieldProps} />
          </FormControl>
        </SimpleGrid>

        <Button
          type="submit"
          fontSize="sm"
          bg={GOLD_BORDER}
          color="#1A1A1A"
          _hover={{ bg: GOLD_TEXT, color: "white" }}
          _active={{ bg: GOLD_TEXT, color: "white" }}
          fontWeight="500"
          w="100%"
          h="50px"
          mt="20px"
          mb="8px"
          isLoading={isSubmitting}
        >
          {t("Create Sub Category")}
        </Button>
        {error && (
          <Box color="red.500" mt={2} whiteSpace="pre-line">
            {error}
          </Box>
        )}
      </form>
    </FormControl>
  );
}

export default CreateSubCategory;
